import { animate, motion, useMotionValue, useReducedMotion } from "motion/react";
import { useEffect, useState, type ReactNode } from "react";
import useMeasure from "react-use-measure";

export interface InfiniteSliderProps {
  children: ReactNode;
  className?: string;
  direction?: "horizontal" | "vertical";
  gap?: number;
  reverse?: boolean;
  speed?: number;
  pauseOnHover?: boolean;
}

export default function InfiniteSlider({
  children,
  gap = 16,
  speed = 36,
  pauseOnHover = true,
  direction = "horizontal",
  reverse = false,
  className,
}: InfiniteSliderProps) {
  const [ref, { width, height }] = useMeasure();
  const translation = useMotionValue(0);
  const [paused, setPaused] = useState(false);
  const [cycle, setCycle] = useState(0);
  const shouldReduceMotion = useReducedMotion();

  useEffect(() => {
    if (shouldReduceMotion) {
      translation.set(0);
      return;
    }
    if (paused) return;

    const size = direction === "horizontal" ? width : height;
    if (!size) return;

    const contentSize = (size + gap) / 2;
    const from = reverse ? -contentSize : 0;
    const to = reverse ? 0 : -contentSize;
    let current = translation.get();
    const completedCycle = reverse ? current >= to - 0.5 : current <= to + 0.5;
    const outsideCycle = reverse ? current < from : current > from;
    if (completedCycle || outsideCycle) {
      current = from;
      translation.set(from);
    }
    const controls = animate(translation, [current, to], {
      duration: Math.abs(to - current) / speed,
      ease: "linear",
      onComplete: () => setCycle((current) => current + 1),
    });

    return controls.stop;
  }, [
    translation,
    width,
    height,
    gap,
    direction,
    reverse,
    speed,
    paused,
    shouldReduceMotion,
    cycle,
  ]);

  return (
    <div
      className={["infinite-slider", className].filter(Boolean).join(" ")}
      data-direction={direction}
      data-reduced-motion={shouldReduceMotion ? "true" : "false"}
      onMouseEnter={() => pauseOnHover && setPaused(true)}
      onMouseLeave={() => pauseOnHover && setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setPaused(false);
      }}
    >
      <motion.div
        className="infinite-slider-track"
        ref={ref}
        style={{
          ...(direction === "horizontal"
            ? { x: translation }
            : { y: translation }),
          flexDirection: direction === "horizontal" ? "row" : "column",
          gap: `${gap}px`,
        }}
      >
        <div className="infinite-slider-set">{children}</div>
        <div className="infinite-slider-set" aria-hidden="true">
          {children}
        </div>
      </motion.div>
    </div>
  );
}
